import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { WebSocketMessageType } from 'src/app/models/enums/websocket-message-type';
import { Authenticate } from 'src/app/models/server/authenticate';
import { CommentsChangeNotification } from 'src/app/models/ws/notifications/comments-change-notification';
import { DocumentAddNotification } from 'src/app/models/ws/notifications/document-add-notification';
import { DocumentDeleteNotification } from 'src/app/models/ws/notifications/document-delete-notification';
import { DocumentUpdateNotification } from 'src/app/models/ws/notifications/document-update-notification';
import { FolderAddNotification } from 'src/app/models/ws/notifications/folder-add-notification';
import { FolderAttributeModificationNotification } from 'src/app/models/ws/notifications/folder-attribute-modification-notification';
import { FolderCloneNotification } from 'src/app/models/ws/notifications/folder-clone-notification';
import { FolderDeleteNotification } from 'src/app/models/ws/notifications/folder-delete-notification';
import { FolderHistoryChangeNotification } from 'src/app/models/ws/notifications/folder-history-change-notification';
import { FolderLockNotification } from 'src/app/models/ws/notifications/folder-lock-notification';
import { FolderMergeNotification } from 'src/app/models/ws/notifications/folder-merge-notification';
import { FolderMoveToBoxNotification } from 'src/app/models/ws/notifications/folder-move-to-box-notification';
import { FolderReadStatusToggleNotification } from 'src/app/models/ws/notifications/folder-read-status-toggle-notification';
import { FolderStandByStateToggleNotification } from 'src/app/models/ws/notifications/folder-stand-by-state-toggle-notification';
import { FolderUnlockRequestNotification } from 'src/app/models/ws/notifications/folder-unlock-request-notification';
import { FolderUnlockResponseNotification } from 'src/app/models/ws/notifications/folder-unlock-response-notification';
import { FolderWaitingArchivingStateToggleNotification } from 'src/app/models/ws/notifications/folder-waiting-archiving-state-toggle-notification';
import { ForceDisconnectionNotification } from 'src/app/models/ws/notifications/force-disconnection-notification';
import { WorkflowDraftEmailNotification } from 'src/app/models/ws/notifications/workflow-draft-email-notification';
import { WorkflowProcessingErrorNotification } from 'src/app/models/ws/notifications/workflow-processing-error-notification';
import { WorkflowProcessingNotification } from 'src/app/models/ws/notifications/workflow-processing-notification';
import { WorkflowStepActivationNotification } from 'src/app/models/ws/notifications/workflow-step-activation-notification';
import { WorkflowStepCompletionNotification } from 'src/app/models/ws/notifications/workflow-step-completion-notification';
import { WorkflowStepStateModificationNotification } from 'src/app/models/ws/notifications/workflow-step-state-modification-notification';
import { OutputWebSocketMessage } from 'src/app/models/ws/output-websocket-message';
import { WebSocketMessage } from 'src/app/models/ws/websocket-message';
import { WebSocketNotificationMessage } from 'src/app/models/ws/websocket-notification-message';
import { environment } from 'src/environments/environment';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { NotificationHandlerService } from './notification-handler.service';

@Injectable({
    providedIn: 'root',
})
export class WebSocketNotificationService {
    baseUrl;

    private socket: WebSocket;
    message$: Subject<WebSocketNotificationMessage> = new Subject<WebSocketNotificationMessage>();

    private pingInterval: any;
    private reconnectInterval: any;
    private readonly PING_INTERVAL_MS = 30000; // 30 seconds
    private readonly RECONNECT_INTERVAL_MS = 5000; // 5 seconds
    private isWebSocketOpen = false;

    /**
     * Constructeur
     */
    constructor(
        private notificationHandlerService: NotificationHandlerService,
        private connectedUserStateService: ConnectedUsersStateService
    ) {
        if (environment.production) {
            var loc = window.location;
            if (loc.protocol === 'https:') {
                this.baseUrl = 'wss:';
            } else {
                this.baseUrl = 'ws:';
            }
            this.baseUrl += '//' + loc.host;
        } else {
            this.baseUrl = environment.wsUrl;
        }
    }

    /**
     * Ouverture d'une webSocket
     * @param login : Login de l'utilisateur
     */
    public startSocket(organizationId: string, login: string) {
        console.log('startSocket');
        const user: Authenticate = this.connectedUserStateService.getUserConnected();

        this.socket = new WebSocket(`${this.baseUrl}/notification/ws?user=${login}&organizationId=${organizationId}&token=${user.token.accessToken}`);

        this.socket.addEventListener('open', (ev) => {
            this.isWebSocketOpen = true;
            console.log('!!WEBSOCKET!! - Websocket notification opened', ev);
            this.startPing();
            if (this.reconnectInterval) {
                clearInterval(this.reconnectInterval);
                this.reconnectInterval = null;
            }
        });

        this.socket.addEventListener('message', (ev) => {
            console.log('!!WEBSOCKET!! - Websocket notification received', ev);
            var messageWrapper: WebSocketMessage = WebSocketMessage.fromJson(JSON.parse(ev.data));
            switch (messageWrapper.messageType) {
                case WebSocketMessageType.Message:
                    let notificationMessage: WebSocketNotificationMessage = WebSocketNotificationMessage.fromJson(JSON.parse(messageWrapper.payload));
                    this.message$.next(notificationMessage);
                    break;
                case WebSocketMessageType.FolderClone:
                    let folderCloneNotification: FolderCloneNotification = FolderCloneNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderCloneNotification`, folderCloneNotification);

                    this.notificationHandlerService.handleFolderCloneNotification(folderCloneNotification);
                    break;
                case WebSocketMessageType.WorkflowStepActivation:
                    let workflowStepActivationNotification: WorkflowStepActivationNotification = WorkflowStepActivationNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification WorkflowStepActivationNotification`, workflowStepActivationNotification);

                    this.notificationHandlerService.handleWorkflowStepActivationNotification(workflowStepActivationNotification);
                    break;
                case WebSocketMessageType.WorkflowStepCompletion:
                    let workflowStepCompletionNotification: WorkflowStepCompletionNotification = WorkflowStepCompletionNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification WorkflowStepCompletionNotification`, workflowStepCompletionNotification);

                    this.notificationHandlerService.handleWorkflowStepCompletionNotification(workflowStepCompletionNotification);
                    break;
                case WebSocketMessageType.WorkflowStepStateModification:
                    let workflowStepStateModificationNotification: WorkflowStepStateModificationNotification = WorkflowStepStateModificationNotification.fromJson(
                        JSON.parse(messageWrapper.payload)
                    );

                    //console.warn(`Réception d'une notification WorkflowStepStateModification`, workflowStepStateModificationNotification);

                    this.notificationHandlerService.handleWorkflowStepStateModificationNotification(workflowStepStateModificationNotification);
                    break;
                case WebSocketMessageType.FolderMerge:
                    let folderMergeNotification: FolderMergeNotification = FolderMergeNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderMergeNotification`, folderMergeNotification);

                    this.notificationHandlerService.handleFolderMergeNotification(folderMergeNotification);
                    break;
                case WebSocketMessageType.FolderMoveToBox:
                    let folderMoveToBoxNotification: FolderMoveToBoxNotification = FolderMoveToBoxNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderMoveToBoxNotification`, folderMoveToBoxNotification);

                    this.notificationHandlerService.handleFolderMoveToBoxNotification(folderMoveToBoxNotification);

                    break;
                case WebSocketMessageType.FolderReadStatusToggle:
                    let folderReadStatusToggleNotification: FolderReadStatusToggleNotification = FolderReadStatusToggleNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderReadStatusToggleNotification`, folderReadStatusToggleNotification);

                    this.notificationHandlerService.handleFolderReadStatusToggleNotification(folderReadStatusToggleNotification);
                    break;
                case WebSocketMessageType.StandByStateToggle:
                    let folderStandByStateToggleNotification: FolderStandByStateToggleNotification = FolderStandByStateToggleNotification.fromJson(
                        JSON.parse(messageWrapper.payload)
                    );

                    //console.warn(`Réception d'une notification FolderStandByStateToggleNotification`, folderStandByStateToggleNotification);

                    this.notificationHandlerService.handleFolderStandByStateToggleNotification(folderStandByStateToggleNotification);
                    break;
                case WebSocketMessageType.WaitingArchivingStateToggle:
                    let folderWaitingArchivingStateToggleNotification: FolderWaitingArchivingStateToggleNotification = FolderWaitingArchivingStateToggleNotification.fromJson(
                        JSON.parse(messageWrapper.payload)
                    );

                    //console.warn(`Réception d'une notification FolderWaitingArchivingStateToggleNotification`, folderWaitingArchivingStateToggleNotification);

                    this.notificationHandlerService.handleFolderWaitingArchivingStateToggleNotification(folderWaitingArchivingStateToggleNotification);
                    break;
                case WebSocketMessageType.FolderAttributeModification:
                    let folderAttributeModificationNotification: FolderAttributeModificationNotification = FolderAttributeModificationNotification.fromJson(
                        JSON.parse(messageWrapper.payload)
                    );

                    //console.warn(`Réception d'une notification FolderAttributeModification`, folderAttributeModificationNotification);

                    this.notificationHandlerService.handleFolderAttributeModificationNotification(folderAttributeModificationNotification);
                    break;
                case WebSocketMessageType.FolderDelete:
                    let folderDeleteNotification: FolderDeleteNotification = FolderDeleteNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderDeleteNotification`, folderDeleteNotification);

                    this.notificationHandlerService.handleFolderDeleteNotification(folderDeleteNotification);
                    break;
                case WebSocketMessageType.FolderAdd:
                    let folderAddNotification: FolderAddNotification = FolderAddNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderAddNotification`, folderAddNotification);

                    this.notificationHandlerService.handleFolderAddNotification(folderAddNotification);
                    break;
                case WebSocketMessageType.DocumentAdd:
                    let documentAddNotification: DocumentAddNotification = DocumentAddNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification DocumentAdd`, documentAddNotification);

                    this.notificationHandlerService.handleDocumentAddNotification(documentAddNotification);
                    break;
                case WebSocketMessageType.DocumentDelete:
                    let documentDeleteNotification: DocumentDeleteNotification = DocumentDeleteNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification DocumentDocumentDelete`, documentDeleteNotification);

                    this.notificationHandlerService.handleDocumentDeleteNotification(documentDeleteNotification);
                    break;
                case WebSocketMessageType.DocumentUpdate:
                    let documentUpdateNotification: DocumentUpdateNotification = DocumentUpdateNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification DocumentUpdate`, documentUpdateNotification);

                    this.notificationHandlerService.handleDocumentUpdateNotification(documentUpdateNotification);
                    break;
                case WebSocketMessageType.FolderLock:
                    let folderLockNotification: FolderLockNotification = FolderLockNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderLockNotification`, folderLockNotification);

                    this.notificationHandlerService.handleFolderLockNotification(folderLockNotification);
                    break;
                case WebSocketMessageType.WorkflowProcessing:
                    let workflowProcessingNotification: WorkflowProcessingNotification = WorkflowProcessingNotification.fromJson(JSON.parse(messageWrapper.payload));

                    this.notificationHandlerService.handleWorkflowProcessingNotification(workflowProcessingNotification);
                    break;
                case WebSocketMessageType.WorkflowProcessingError:
                    let workflowProcessingErrorNotification: WorkflowProcessingErrorNotification = WorkflowProcessingErrorNotification.fromJson(JSON.parse(messageWrapper.payload));

                    this.notificationHandlerService.handleWorkflowProcessingErrorNotification(workflowProcessingErrorNotification);
                    break;
                case WebSocketMessageType.FolderHistoryChange:
                    let historiesFolderChangeNotification: FolderHistoryChangeNotification = FolderHistoryChangeNotification.fromJson(JSON.parse(messageWrapper.payload));

                    this.notificationHandlerService.handleFolderHistoryChangeNotification(historiesFolderChangeNotification);
                    break;
                case WebSocketMessageType.FolderUnlockRequest:
                    let folderUnlockRequestNotification: FolderUnlockRequestNotification = FolderUnlockRequestNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderUnlockRequestNotification`, folderUnlockRequestNotification);

                    this.notificationHandlerService.handleFolderUnlockRequestNotification(folderUnlockRequestNotification);
                    break;
                case WebSocketMessageType.FolderUnlockResponse:
                    let folderUnlockResponseNotification: FolderUnlockResponseNotification = FolderUnlockResponseNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification FolderUnlockResponseNotification`, folderUnlockResponseNotification);

                    this.notificationHandlerService.handleFolderUnlockResponseNotification(folderUnlockResponseNotification);
                    break;
                case WebSocketMessageType.CommentsChange:
                    let commentsChangeNotification: CommentsChangeNotification = CommentsChangeNotification.fromJson(JSON.parse(messageWrapper.payload));

                    this.notificationHandlerService.handleCommentsChangeNotification(commentsChangeNotification);
                    break;
                case WebSocketMessageType.ForceDisconnection:
                    let forceDisconnectionNotification: ForceDisconnectionNotification = ForceDisconnectionNotification.fromJson(JSON.parse(messageWrapper.payload));

                    this.notificationHandlerService.handleForceDisconnectionNotification(forceDisconnectionNotification, this);
                    break;
                case WebSocketMessageType.DraftEmailCreation:
                    let workflowDraftEmailNotification: WorkflowDraftEmailNotification = WorkflowDraftEmailNotification.fromJson(JSON.parse(messageWrapper.payload));

                    //console.warn(`Réception d'une notification DraftEmailCreation`, workflowDraftEmailNotification);

                    this.notificationHandlerService.handleWorkflowDraftEmailNotification(workflowDraftEmailNotification);
                    break;
                default:
                    console.error(`Réception d'une notification inconnue`, messageWrapper);
                    break;
            }
        });

        this.socket.addEventListener('close', (ev) => {
            if (this.isWebSocketOpen) {
                console.log('!!WEBSOCKET!! - Websocket notification was closed', ev);
                this.stopPing();
                this.reconnect(organizationId, login);
            }

            this.isWebSocketOpen = false;
        });

        this.socket.addEventListener('error', (ev) => {
            if (this.isWebSocketOpen) {
                console.log('!!WEBSOCKET!! - Websocket notification raise an error', ev);
                this.stopPing();
                this.reconnect(organizationId, login);
            }

            this.isWebSocketOpen = false;
        });
    }

    private startPing() {
        console.log('!!WEBSOCKET!! - Start ping');
        this.pingInterval = setInterval(() => {
            console.log('Declenchement ping');
            if (this.socket.readyState === WebSocket.OPEN) {
                console.log('Send ping');
                this.socket.send(JSON.stringify({ type: 'ping' }));
            }
        }, this.PING_INTERVAL_MS);
    }

    private stopPing() {
        if (this.pingInterval) {
            console.log('!!WEBSOCKET!! - Stop ping');
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    private reconnect(organizationId: string, login: string) {
        console.log('!!WEBSOCKET!! - Reconnect');
        if (this.reconnectInterval) {
            return; // Already attempting to reconnect
        }

        this.reconnectInterval = setInterval(() => {
            console.log('!!WEBSOCKET!! - Attempting to reconnect...');
            this.startSocket(organizationId, login);
        }, this.RECONNECT_INTERVAL_MS);
    }

    /**
     * Arret de la webSocket
     * @param login : Login de l'utilisateur
     */
    public stopSocket(login: string) {
        console.log('!!WEBSOCKET!! - Demande au serveur la deconnexion de la Websocket notification');
        this.sendMessageRequest(new OutputWebSocketMessage('disconnect', null));
        this.socket.close();
        this.stopPing();
        if (this.reconnectInterval) {
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = null;
        }
    }

    /**
     * Envoi d'une requête par la webSocket;
     * @param req : Requête a envoyé
     */
    public sendMessageRequest(req: OutputWebSocketMessage) {
        var requestAsJson = JSON.stringify(req);
        this.socket.send(requestAsJson);
    }
}
